import curry from "../../../lib/core/functional/curry";
import { doUpgradeToLoggedIn } from "../../../upgradeToLoggedInLoader";

import {
    signupRequest,
    loginRequest,
    checkMFAConfig,
    verifyOtp,
    resetPasswordRequest,
} from "../../../transport/loginSignupV2";

import {
    getDefaultLoginTemplate,
    getDefaultSignupTemplate,
    getDefaultResetTemplate,
    getDefaultOtpTemplate,
} from "./templates";

import { loginModules, signupModules, resetModules, otpModules } from "./modules";

import getBlocker from "./blockers/index.js";

function removeModuleNode(node) {
    node.remove();
    return null;
}

function renderModules(form, moduleMap, moduleNode) {
    const { module } = moduleNode.dataset;

    try {
        const { name, params } = JSON.parse(unescape(module));
        return name in moduleMap
            ? moduleMap[name](form, moduleNode, params)
            : removeModuleNode(moduleNode);
    } catch (error) {
        console.log(error);
        return removeModuleNode(moduleNode);
    }
}

const renderModuleOnForm = curry(renderModules);

function modulesFactory(form, classSelector, moduleMap) {
    return Array.from(form.querySelectorAll(classSelector))
        .map(renderModuleOnForm(form, moduleMap))
        .filter(Boolean);
}

function valueGettersReducer(store, setter) {
    return setter(store);
}

function formValuesToStore(valueGetters) {
    return valueGetters.reduce(valueGettersReducer, {});
}

function emitCustomEvent(eventName, element, payload) {
    const event = new CustomEvent(eventName, { detail: payload });
    element.dispatchEvent(event);
}

const emitEventOn = curry(emitCustomEvent);

function handleFormSubmit(nodeId, form, valueGetters, action, event) {
    event.preventDefault();

    action(nodeId, form, formValuesToStore(valueGetters)).catch(
        emitEventOn("authentication:error", form)
    );
}

const formSubmitFactory = curry(handleFormSubmit);

function setUpForm(nodeId, form, active, moduleSelector, modules, action) {
    if (!active) form.classList.add("hidden");
    const valueGetters = modulesFactory(form, moduleSelector, modules);

    emitCustomEvent("authentication:rendered", form, form);

    form.addEventListener("submit", formSubmitFactory(nodeId, form, valueGetters, action));
}

function addDefaultFormToWrapper(wrapperElement, selectorClass, template) {
    const element = document.createElement("form");
    element.classList.add("authentication--form");
    element.classList.add(selectorClass);
    element.innerHTML = template;
    wrapperElement.appendChild(element);
    return element;
}

function getRenderedFormOrTemplate(wrapperElement, selectorClass, templateGetter) {
    const form = wrapperElement.querySelector(`.${selectorClass}`);
    if (form && form.children.length) {
        form.classList.add("authentication--form");
        return form;
    }
    return addDefaultFormToWrapper(wrapperElement, selectorClass, templateGetter());
}

const onLoginSuccess = curry(function onLoginSuccess(context, form, emiter, redirect) {
    if (form.action === "#") {
        emiter({ message: "Login successfull! You are logged in now" });
        doUpgradeToLoggedIn(context);
    } else if (form.getAttribute("data-force-redirect") === "true") {
        emiter({ message: "Login successfull! You will be redirected shortly" });
        window.location.href = redirect;
    } else {
        emiter({ message: "Login successfull! You will be redirected shortly" });
        window.location.href = form.getAttribute("action") || redirect || form.action;
    }
});

const checkForMFA = curry(function checkForMFA(form, context, response) {
    if (response.is_otp_verification_requested) {
        const otpForm = form.parentElement.querySelector("form.otp-form");
        emitCustomEvent("mfa:enabled", otpForm);
    } else {
        onLoginSuccess(
            context,
            form,
            emitEventOn("authentication:success", form),
            response.redirect_to
        );
    }
});

const onLoginSubmit = curry(function onLoginSubmit(blocker, context, nodeId, form, body) {
    const requestBody = {
        node_id: nodeId,
        ...body.authorization,
    };

    function onRequestSecured(requestBody) {
        return loginRequest(requestBody, document.location.search);
    }

    return blocker
        .secureRequest(requestBody)
        .then(onRequestSecured)
        .then(checkForMFA(form, context));
});

const onResetSuccess = curry(function onResetSuccess(_form, emiter, _response) {
    emiter({ message: "We have sent an email with instructions to reset your password" });
});

const onResetSubmit = curry(function onResetSubmit(blocker, nodeId, form, body) {
    const requestBody = {
        node_id: nodeId,
        post_reset_redirect:
            form.getAttribute("action") || window.location.pathname + window.location.search,
        ...body.authorization,
    };

    return blocker
        .secureRequest(requestBody)
        .then(resetPasswordRequest)
        .then(onResetSuccess(form, emitEventOn("authentication:success", form)));
});

const onSignupSuccess = curry(function onSignupSuccess(context, form, emiter, response) {
    if (form.action === "#") {
        emiter({ message: "Signup successfull! You are logged in now!" });
        doUpgradeToLoggedIn(context);
    } else {
        emiter({ message: "Signup successfull! You will be redirected shortly" });
        window.location.href =
            form.getAttribute("action") || response.post_setup_redirect || form.action;
    }
});

const onSignupSubmit = curry(function onSignupSubmit(blocker, context, nodeId, form, body) {
    body.node_id = nodeId;

    return blocker
        .secureRequest(body)
        .then(signupRequest)
        .then(onSignupSuccess(context, form, emitEventOn("authentication:success", form)));
});

function onOtpCorrect(context, body) {
    const loginForm = body.parentElement.querySelector(".login-form");
    emitCustomEvent("otp:valid", body);
    const nextUrl = loginForm.action;

    onLoginSuccess(context, loginForm, emitEventOn("authentication:success", loginForm), nextUrl);
}

function onOtpIncorrect(body) {
    emitCustomEvent("otp:invalid", body);
}

function processOtpResponse(context, body, response) {
    if (response && typeof response === "object" && Object.keys(response).length === 0) {
        return onOtpCorrect(context, body);
    }

    return onOtpIncorrect(body);
}

const onOtpSubmit = curry(function onSignupSubmit(_blocker, context, _nodeId, body, _form) {
    const otpCode = {
        code: body.querySelector("input").value,
    };

    function handleResponse(response) {
        processOtpResponse(context, body, response);
    }

    return verifyOtp(otpCode).then(handleResponse).catch(emitEventOn("otp:invalid", body));
});

export default function loginSignupForm(context, wrapperElement) {
    const initialState = wrapperElement.dataset.initialState || "signup";
    const nodeId = wrapperElement.dataset.nodeId;

    const blocker = getBlocker();

    setUpForm(
        nodeId,
        getRenderedFormOrTemplate(wrapperElement, "login-form", getDefaultLoginTemplate),
        initialState === "login",
        ".login-module",
        loginModules,
        onLoginSubmit(blocker, context)
    );

    setUpForm(
        nodeId,
        getRenderedFormOrTemplate(wrapperElement, "signup-form", getDefaultSignupTemplate),
        initialState === "signup",
        ".signin-module",
        signupModules,
        onSignupSubmit(blocker, context)
    );

    setUpForm(
        nodeId,
        getRenderedFormOrTemplate(wrapperElement, "reset-form", getDefaultResetTemplate),
        initialState === "reset",
        ".reset-module",
        resetModules,
        onResetSubmit(blocker)
    );

    setUpForm(
        nodeId,
        getRenderedFormOrTemplate(wrapperElement, "otp-form", getDefaultOtpTemplate),
        initialState === "otp",
        ".otp-module",
        otpModules,
        onOtpSubmit(blocker, context)
    );
}
